import { all, call, fork, put, select, takeEvery } from "redux-saga/effects";
import { GET, POST, PUT } from "../../helpers/apiHelper";
import * as action from "./actions";
import { storeLog } from "../log/actions";
import i18n from "../../i18n";

export const getListState = (state) => state.blobSettingApp.list;

const loadBlobSettingAsync = async (query) => GET(`/blob-setting/getall?${query}`);
const storeBlobSettingAsync = async (item) => POST(`/blob-setting/store`, item, { idempotent: true });
const editBlobSettingAsync = async (id, item) => PUT(`/blob-setting/update/${id}`, item);
const deleteBlobSettingAsync = async (IDs) => POST(`/blob-setting/destroy`, IDs);

/**
 * Worker saga for loading blob settings.
 * Triggered when BLOB_SETTING_GET_LIST action is dispatched.
 *
 * @generator
 * @param {{ payload: Object }} action - Redux action with payload (filters, pagination, etc.).
 */
function* loadBlobSettingList({ payload }) {
  try {
    // console.log('loadBlobSettingList', payload)
    const list = yield select(getListState);
    let param = [
      {
        name: "page",
        value: payload.selectedPage,
      },
      {
        name: "orderby",
        value: list.orderBy,
      },
      {
        name: "sortedby",
        value: list.sortedBy,
      },
      {
        name: "limit",
        value: payload.limit,
      },
      {
        name: "show_all",
        value: encodeURIComponent(payload?.showAll),
      },
    ];

    if (list.filter || "") {
      param = [
        ...param,
        ...[
          {
            name: "search",
            value: list.filter,
          },
        ],
      ];
    }

    const response = yield call(
      loadBlobSettingAsync,
      param.map((t) => `${t.name}=${t.value}`).join("&")
    );
    yield put(action.loadBlobSettingSuccess(response));
  } catch (error) {
    yield put(action.loadBlobSettingError(error));
  }
}

/**
 * Worker saga for saving (add/edit) blob settings.
 * Triggered when BLOB_SETTING_SAVE_ADD_EDIT action is dispatched.
 *
 * @generator
 * @param {{ payload: { item: Object, mode: "add"|"edit", history: Object } }} action - Redux action with payload.
 */
function* saveAddeditBlobSetting({ payload }) {
  let response = null;
  try {
    console.log('payload', payload)
    if (payload.item) {
      const payloadStore = payload.item;

      if (payload.mode === "edit" && payloadStore.id) {
        response = yield call(editBlobSettingAsync, payloadStore.id, payloadStore);
      } else if (payload.mode === "add") {
        response = yield call(storeBlobSettingAsync, payloadStore);
      } else {
        throw new Error("invalid mode");
      }
      console.log('response', response)
      yield put(action.saveAddEditBlobSettingSuccess(response));
      
      // Reload the blob setting list
      let storedPage = localStorage.getItem("BLOB_SETTING_PAGE")
      if(storedPage){
        yield put(action.loadBlobSetting({
          selectedPage: parseFloat(storedPage)
        }));
      }else{
        yield put(action.loadBlobSetting());
      }  
      

      /* save log - skip if only status update */
      const isStatusOnlyUpdate = payload.item && 
        payload.item.status !== undefined && 
        !payload.item.displayName &&
        !payload.item.containerName &&
        !payload.item.accountName &&
        !payload.item.accountKey;

      if (!isStatusOnlyUpdate) {
        yield put(
          storeLog({
            type: "BLOB MANAGEMENT",
            title: payload.mode === "edit" ? i18n.t("blobSetting.alert.updateSuccess") : i18n.t("blobSetting.alert.createSuccess"),
            status: "INFO",
            detail: payload.item,
          })
        );
      }
      /* save log */

      // Only navigate if history.push is a real function (not a mock for status update)
      const { history } = payload;
      if (history && typeof history.push === 'function' && history.push.toString().indexOf('Mock') === -1) {
        history.push(`/blob-setting`);
      }
    } else {
      throw new Error(i18n.t("blobSetting.alert.invalidSaveItem"));
    }
  } catch (error) {
    /* save log */
    yield put(
      storeLog({
        type: "BLOB MANAGEMENT",
        title: i18n.t("blobSetting.alert.createFailed"),
        status: "ERROR",
        detail: {
          ...payload.item,
          message: error?.message
        },
      })
    );
    /* save log */

    console.log('saveAddEditBlobSettingError', error)
    yield put(
      action.saveAddEditBlobSettingError(error?.response?.data?.message || error)
    );
  }
}

/**
 * Worker saga for deleting blob settings.
 * Triggered when BLOB_SETTING_DELETE action is dispatched.
 *
 * @generator
 * @param {{ payload: { data: Object, confirmed: boolean } }} action - Redux action with payload.
 */
function* deleteBlobSetting({ payload }) {
  try {
    if (payload && payload.confirmed) {
      if (payload.item) {
        yield call(deleteBlobSettingAsync, payload.item);
        yield put(action.deleteBlobSettingSuccess());

        yield put(action.loadBlobSetting());
      } else {
        throw new Error("can't delete blob setting");
      }
    }

    /* save log */
    yield put(
      storeLog({
        type: "BLOB MANAGEMENT",
        title: i18n.t("blobSetting.alert.deleteSuccess"),
        status: "INFO",
        detail: payload.item,
      })
    );
    /* save log */
  } catch (error) {
    const errorMessage = error.response?.data?.message || i18n.t("blobSetting.alert.unknownError");
    /* save log */
    yield put(
      storeLog({
        type: "BLOB MANAGEMENT",
        title: i18n.t("blobSetting.alert.deleteFailed"),
        status: "ERROR",
        detail: {
          ...payload.item,
          message: errorMessage
        },
      })
    );
    /* save log */
    yield put(action.deleteBlobSettingError(errorMessage));
  }
}

/**
 * Watcher saga for BLOB_SETTING_GET_LIST.
 * Spawns a new loadBlobSettingList task on each dispatched action.
 *
 * @generator
 */
export function* watchloadBlobSettingList() {
  yield takeEvery(action.BLOB_SETTING_GET_LIST, loadBlobSettingList);
}

/**
 * Watcher saga for BLOB_SETTING_SAVE_ADD_EDIT.
 * Spawns a new saveAddeditBlobSetting task on each dispatched action.
 *
 * @generator
 */
export function* watchSaveAddeditBlobSetting() {
  yield takeEvery(action.BLOB_SETTING_SAVE_ADD_EDIT, saveAddeditBlobSetting);
}

/**
 * Watcher saga for BLOB_SETTING_DELETE.
 * Spawns a new deleteBlobSetting task on each dispatched action.
 *
 * @generator
 */
export function* watchDeleteBlobSetting() {
  yield takeEvery(action.BLOB_SETTING_DELETE, deleteBlobSetting);
}

/**
 * Root saga for blob settings.
 * Forks all watcher sagas related to blob setting actions.
 *
 * @generator
 */
export default function* rootSaga() {
  yield all([
    fork(watchloadBlobSettingList),
    fork(watchSaveAddeditBlobSetting),
    fork(watchDeleteBlobSetting),
  ]);
}